/**
 * Profile Completeness Calculator - Single Source of Truth
 * Used in: AdminMSPTable, DashboardHome, DashboardStatistieken, Vergelijken sort
 */

export interface ProfileField {
  label: string;
  filled: boolean;
}

export interface ProfileSection {
  name: string;
  complete: boolean;
  fields: ProfileField[];
}

export interface ProfileCompletenessResult {
  percentage: number;
  sections: ProfileSection[];
  filledCount: number;
  totalCount: number;
}

/**
 * Calculates profile completeness for an MSP.
 * Accepts any object with the relevant MSP fields.
 */
export function calculateProfileCompleteness(msp: Record<string, any>): ProfileCompletenessResult {
  const sections: ProfileSection[] = [
    {
      name: 'Bedrijfsprofiel',
      complete: false,
      fields: [
        { label: 'Logo', filled: !!msp.logo_url },
        { label: 'Naam', filled: !!msp.naam },
        { label: 'Locatie', filled: !!msp.plaats },
        { label: 'Website', filled: !!msp.website },
        { label: 'E-mail', filled: !!msp.email },
        { label: 'Telefoon', filled: !!msp.telefoon },
        { label: 'Aantal medewerkers', filled: !!msp.aantal_medewerkers },
        { label: 'Opgericht', filled: !!msp.oprichtingsjaar },
        { label: 'Responstijd', filled: !!msp.response_time_tier },
      ],
    },
    {
      name: 'Doelgroep & Diensten',
      complete: false,
      fields: [
        { label: 'Doelgroep segment', filled: Array.isArray(msp.doelgroep_segment) && msp.doelgroep_segment.length > 0 },
        { label: 'Branches', filled: Array.isArray(msp.branches) && msp.branches.length > 0 },
        { label: 'Diensten', filled: Array.isArray(msp.diensten) && msp.diensten.length > 0 },
        { label: 'Samenwerkingsmodel', filled: Array.isArray(msp.engagement_models) && msp.engagement_models.length > 0 },
      ],
    },
    {
      name: 'Content',
      complete: false,
      fields: [
        { label: 'Omschrijving', filled: !!msp.uitgebreide_omschrijving },
        { label: 'Tech stack', filled: !!msp.techstack },
        { label: 'Klantcases', filled: !!msp.klantcases },
      ],
    },
    {
      name: 'Compliance',
      complete: false,
      fields: [
        { label: 'Certificeringen', filled: Array.isArray(msp.certificeringen) && msp.certificeringen.length > 0 },
        {
          label: 'NIS2 capabilities (≥4/8)',
          filled: countNIS2Flags(msp) >= 4,
        },
      ],
    },
  ];

  let filledCount = 0;
  let totalCount = 0;

  for (const section of sections) {
    const sectionFilled = section.fields.filter(f => f.filled).length;
    totalCount += section.fields.length;
    filledCount += sectionFilled;
    section.complete = sectionFilled === section.fields.length;
  }

  const percentage = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;

  return { percentage, sections, filledCount, totalCount };
}

/**
 * Count how many of the 8 core NIS2 flags are true
 */
function countNIS2Flags(msp: Record<string, any>): number {
  const flags = [
    'nis2_werkplek_identity',
    'nis2_support_incident',
    'nis2_backup_bcdr',
    'nis2_security_awareness',
    'nis2_cloud_infra',
    'nis2_patch_vulnerability',
    'nis2_network_access',
    'nis2_detection_monitoring',
  ];
  return flags.filter(f => msp[f] === true).length;
}
